// CountdownScreen.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./CountdownScreen.css";
import beatUrl from "../assets/beat.mp3";
import { loadCountdownPayloadFromStore } from "../utils/countdownPayloadStore";
import { downloadWinnerReportPdf } from "../utils/winnerReport";

const COUNTDOWN_PAYLOAD_KEY = "cs_countdown_payload_v1";
const BRANDING_KEY = "cs_countdown_branding_v1";
const WINNER_SHARE_DECIMALS = 4;

export default function CountdownScreen() {
  const navigate = useNavigate();
  const { state } = useLocation();

  // Read payload from localStorage (needed for “open in new tab”)
  const storedPayload = useMemo(() => {
    try {
      const raw = localStorage.getItem(COUNTDOWN_PAYLOAD_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  const [payloadReady, setPayloadReady] = useState(false);
  const [payloadData, setPayloadData] = useState({
    aggregated: [],
    emailKey: "",
    entriesKey: "",
    nameKey: "",
    fileName: "",
    fileUploadedAt: 0,
    reportAnalytics: null,
    exportMetadata: null,
  });

  useEffect(() => {
    let cancelled = false;

    const applyPayload = (payload) => {
      if (cancelled) return;
      const aggregated = Array.isArray(payload?.aggregated) ? payload.aggregated : [];
      const emailKey = payload?.emailKey ? String(payload.emailKey) : "";
      const entriesKey = payload?.entriesKey ? String(payload.entriesKey) : "";
      const nameKey = payload?.nameKey ? String(payload.nameKey) : "";
      const fileName = payload?.fileName ? String(payload.fileName) : "";
      const fileUploadedAt = Number(payload?.fileUploadedAt || 0);
      const reportAnalytics =
        payload?.reportAnalytics && typeof payload.reportAnalytics === "object"
          ? payload.reportAnalytics
          : null;
      const exportMetadata =
        payload?.exportMetadata && typeof payload.exportMetadata === "object"
          ? payload.exportMetadata
          : null;
      setPayloadData({
        aggregated,
        emailKey,
        entriesKey,
        nameKey,
        fileName,
        fileUploadedAt,
        reportAnalytics,
        exportMetadata,
      });
    };

    async function loadPayload() {
      if (Array.isArray(state?.aggregated)) {
        applyPayload(state);
        if (!cancelled) setPayloadReady(true);
        return;
      }

      if (Array.isArray(storedPayload?.aggregated)) {
        applyPayload(storedPayload);
        if (!cancelled) setPayloadReady(true);
        return;
      }

      const drawId = storedPayload?.drawId ? String(storedPayload.drawId) : "";
      if (drawId) {
        try {
          const loaded = await loadCountdownPayloadFromStore(drawId);
          if (cancelled) return;
          applyPayload(loaded);
        } catch {
          if (!cancelled) applyPayload(null);
        } finally {
          if (!cancelled) setPayloadReady(true);
        }
        return;
      }

      applyPayload(null);
      if (!cancelled) setPayloadReady(true);
    }

    void loadPayload();
    return () => {
      cancelled = true;
    };
  }, [state, storedPayload]);

  // ----- Inputs & keys -----
  const aggregated = payloadData.aggregated;

  const initialEntriesKey = useMemo(() => {
    return payloadData.entriesKey || detectEntriesKey(Object.keys(aggregated[0] || {}));
  }, [payloadData.entriesKey, aggregated]);

  const initialNameKey = useMemo(() => {
    return payloadData.nameKey || detectNameKey(Object.keys(aggregated[0] || {}));
  }, [payloadData.nameKey, aggregated]);

  // Guard: missing data -> go back
  useEffect(() => {
    if (!payloadReady) return;
    if (!aggregated.length) navigate("/winner-select", { replace: true });
  }, [payloadReady, aggregated, navigate]);

  // ----- Audio -----
  const audioRef = useRef(null);

  useEffect(() => {
    const el = new Audio(beatUrl);
    el.preload = "auto";
    el.loop = true;
    el.volume = 1;

    const onMeta = () => {
      try {
        el.currentTime = 3;
      } catch {}
    };
    el.addEventListener("loadedmetadata", onMeta);

    audioRef.current = el;

    return () => {
      try {
        el.pause();
      } catch {}
      el.removeEventListener("loadedmetadata", onMeta);
      el.src = "";
    };
  }, []);

  // ----- Countdown control -----
  const [count, setCount] = useState(10);
  const [running, setRunning] = useState(false); // IMPORTANT: do NOT autostart
  const [started, setStarted] = useState(false);
  const [finale, setFinale] = useState(false);
  const [winner, setWinner] = useState(null);
  const [brandingUrl, setBrandingUrl] = useState("");
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const refreshBranding = useCallback(() => {
    try {
      const raw = localStorage.getItem(BRANDING_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      setBrandingUrl(parsed?.dataUrl ? String(parsed.dataUrl) : "");
    } catch {
      setBrandingUrl("");
    }
  }, []);

  useEffect(() => {
    // load on mount
    refreshBranding();
  
    // if WinnerSelect updates branding in another tab/window
    const onStorage = (e) => {
      if (e.key === BRANDING_KEY) refreshBranding();
    };
  
    // if this tab was already open and you come back to it
    const onFocus = () => refreshBranding();
  
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
  
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshBranding]);
  
  // One-shot guard: the draw must never happen twice
  const drawnRef = useRef(false);

  // Real-time anchoring
  const deadlineRef = useRef(0);
  const rafRef = useRef(0);

  // PII reveal
  const [piiVisible, setPiiVisible] = useState(false);
  const holdTimerRef = useRef(null);
  const progTimerRef = useRef(null);
  const [holdMs, setHoldMs] = useState(0);
  const HOLD_DURATION = 1500;
  const holdPct = Math.round((holdMs / HOLD_DURATION) * 100);

  // Total entries
  const totalEntries = useMemo(() => {
    if (!aggregated.length || !initialEntriesKey) return 0;
    return aggregated.reduce(
      (s, r) => s + Math.max(0, toNumber(r[initialEntriesKey])),
      0
    );
  }, [aggregated, initialEntriesKey]);

  const pickWinner = useCallback(() => {
    if (!aggregated.length || !initialEntriesKey) return;
    const idx = pickWeightedIndexForRows(aggregated, initialEntriesKey);
    const row = aggregated[idx];
    const winnerEntries = Math.max(0, toNumber(row?.[initialEntriesKey]));

    setWinner({
      idx,
      row,
      drawnAt: Date.now(),
      name: deriveDisplayName(row, initialNameKey),
      firstLast: deriveFirstLast(row, initialNameKey),
      entries: winnerEntries,
      share:
        totalEntries > 0
          ? ((winnerEntries / totalEntries) * 100).toFixed(WINNER_SHARE_DECIMALS)
          : "0.0000",
    });
  }, [aggregated, initialEntriesKey, initialNameKey, totalEntries]);


  const commitWinner = useCallback(() => {
    if (drawnRef.current) return;
    drawnRef.current = true;
    pickWinner();
  }, [pickWinner]);

  const fadeOutAudio = useCallback((el, duration = 1200) => {
    if (!el) return Promise.resolve();
    let start = null;
    const initialVol = el.volume;
    return new Promise((resolve) => {
      function step(ts) {
        if (start == null) start = ts;
        const t = ts - start;
        const frac = Math.min(t / duration, 1);
        el.volume = initialVol * (1 - frac);
        if (frac < 1) {
          requestAnimationFrame(step);
        } else {
          try {
            el.pause();
          } catch {}
          el.volume = initialVol;
          resolve();
        }
      }
      requestAnimationFrame(step);
    });
  }, []);

  // Countdown anchored to real time; at 0 start the finale, then reveal winner once
  useEffect(() => {
    if (!running || finale) return;

    if (!deadlineRef.current) {
      deadlineRef.current = Date.now() + count * 1000;
    }

    const tick = () => {
      const msLeft = Math.max(0, deadlineRef.current - Date.now());
      const next = Math.ceil(msLeft / 1000);
      setCount((prev) => (prev !== next ? next : prev));

      if (msLeft <= 0) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
        deadlineRef.current = 0;
        setRunning(false);

        (async () => {
          setFinale(true);
          try {
            await fadeOutAudio(audioRef.current, 1200);
          } catch {}
          setTimeout(() => {
            commitWinner();
          }, 300);
        })();

        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [running, finale, count, commitWinner, fadeOutAudio]);

  // Play/pause audio while running
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    if (running && count > 0 && !finale) {
      if (el.currentTime < 3) {
        try {
          el.currentTime = 3;
        } catch {}
      }
      el.play().catch(() => {
        // autoplay blocked; Start button should satisfy user-gesture in most browsers
      });
    } else if (!running && !finale) {
      try {
        el.pause();
      } catch {}
    }
  }, [running, count, finale]);

  function cleanupTimers() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    deadlineRef.current = 0;
    clearTimeout(holdTimerRef.current);
    clearInterval(progTimerRef.current);
  }

  function closeOrGoBack() {
    cleanupTimers();
    try {
      audioRef?.current?.pause?.();
    } catch {}

    // If this was opened as a new tab via window.open, allow closing.
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    navigate("/winner-select");
  }

  function startCountdown() {
    refreshBranding();
    // Reset run state
    cleanupTimers();
    drawnRef.current = false;
    setWinner(null);
    setFinale(false);
    setPiiVisible(false);
    setHoldMs(0);

    setCount(10);
    setStarted(true);
    setRunning(true);

    // Attempt to start audio immediately (user gesture)
    try {
      audioRef.current?.play?.();
    } catch {}
  }

  // ----- Press & hold to reveal PII -----
  function startHold() {
    if (piiVisible) {
      setPiiVisible(false);
      return;
    }
    clearTimeout(holdTimerRef.current);
    clearInterval(progTimerRef.current);
    setHoldMs(0);
    holdTimerRef.current = setTimeout(() => {
      setPiiVisible(true);
      clearInterval(progTimerRef.current);
      setHoldMs(HOLD_DURATION);
    }, HOLD_DURATION);
    progTimerRef.current = setInterval(() => {
      setHoldMs((ms) => Math.min(ms + 50, HOLD_DURATION));
    }, 50);
  }
  function endHold() {
    if (!piiVisible) {
      clearTimeout(holdTimerRef.current);
      clearInterval(progTimerRef.current);
      setHoldMs(0);
    }
  }

  async function downloadWinnerReport() {
    if (!winner) return;

    setIsDownloadingReport(true);
    try {
      downloadWinnerReportPdf({
        payload: payloadData,
        winner,
        drawnAt: winner.drawnAt || Date.now(),
      });
    } finally {
      setIsDownloadingReport(false);
    }
  }

  if (!payloadReady) {
    return <div className="cd-root" />;
  }

  return (
    <div className="cd-root">
      {/* Branding above everything */}
      {brandingUrl && (
        <img className="cd-branding" src={brandingUrl} alt="Branding / logo" />
      )}

      {/* Pre-start screen */}
      {!started && !winner && (
        <section className="cd-stage" aria-live="polite">
          <div className="cd-caption" style={{ marginBottom: 12 }}>
            Ready to select a sweepstakes winner.
          </div>

          <div className="cd-actions" style={{ justifyContent: "center" }}>
            <button className="cd-action" onClick={startCountdown}>
              Start Countdown
            </button>
            <button className="cd-action ghost" onClick={closeOrGoBack}>
              Close
            </button>
          </div>
        </section>
      )}

      {/* Countdown running */}
      {started && !winner && !finale && (
        <section className="cd-stage" aria-live="polite">
          <div className="cd-radar">
            <div className="cd-rings" />
            <div className="cd-sweep" />
            <div className="cd-dot" />
            <div className="cd-count">{count}</div>
          </div>
          <div className="cd-caption">Selecting a sweepstakes winner…</div>
          <button className="cd-action ghost" onClick={closeOrGoBack}>
            Cancel
          </button>
        </section>
      )}

      {/* Finale */}
      {started && !winner && finale && (
        <section className="cd-stage" aria-live="polite">
          <style>{`
            @keyframes cdPulse {
              0% { transform: scale(0.9); opacity: .85; }
              50% { transform: scale(1.05); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            }
          `}</style>
          <div
            style={{
              width: 220,
              height: 220,
              borderRadius: "50%",
              border: "6px solid rgba(255,255,255,.85)",
              boxShadow: "0 0 60px rgba(255,255,255,.25)",
              animation: "cdPulse 0.9s ease-in-out 1",
              margin: "0 auto",
            }}
          />
          <div className="cd-caption" style={{ marginTop: 16 }}>
            Finalizing…
          </div>
        </section>
      )}

      {/* Result */}
      {winner && (
        <section className="cd-result">
          <h1 className="cd-winner-title">Winner</h1>
          <div className="cd-winner-name">{winner.name}</div>

          <div className="cd-meta" style={{ display: "grid", gap: 4 }}>
            <div>
              First: <strong>{winner.firstLast.first || "—"}</strong>
            </div>
            <div>
              Last: <strong>{winner.firstLast.last || "—"}</strong>
            </div>
          </div>

          <div className="cd-blur-wrap" style={{ marginTop: 12 }}>
            <pre
              className="cd-json"
              style={{
                filter: piiVisible ? "none" : undefined,
                userSelect: piiVisible ? "text" : "none",
                pointerEvents: piiVisible ? "auto" : "none",
              }}
            >
              {JSON.stringify(winner.row, null, 2)}
            </pre>

            {piiVisible ? (
              <div style={{ padding: "8px 0 0 0", color: "#cfe3ff" }}>
                Winner’s share: <strong>{winner.share}%</strong>
              </div>
            ) : (
              <div className="cd-blur-overlay">
                Blurred for customer safety. Do not share private customer details
                on a live-stream!
              </div>
            )}
          </div>

          <div className="cd-actions" style={{ alignItems: "center" }}>
            <button
              className="cd-action"
              onMouseDown={startHold}
              onMouseUp={endHold}
              onMouseLeave={endHold}
              onTouchStart={startHold}
              onTouchEnd={endHold}
              onTouchCancel={endHold}
              aria-label={piiVisible ? "Hide sensitive details" : "Press and hold to reveal"}
              title={piiVisible ? "Hide sensitive details" : "Press and hold to reveal"}
            >
              {piiVisible ? "Hide PII" : "Press & Hold to Reveal PII"}
            </button>

            {!piiVisible && (
              <div
                aria-hidden
                style={{
                  width: 140,
                  height: 6,
                  borderRadius: 999,
                  background: "rgba(255,255,255,.15)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${holdPct}%`,
                    height: "100%",
                    background: "rgba(69,163,255,.9)",
                    transition: "width .05s linear",
                  }}
                />
              </div>
            )}

            <button className="cd-action" onClick={closeOrGoBack}>
              Close
            </button>
          </div>

          <div className="cd-actions" style={{ justifyContent: "center", marginTop: 16 }}>
            <button className="cd-action" onClick={downloadWinnerReport}>
              {isDownloadingReport ? "Preparing Report..." : "Download Winner Report"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

/* ----------------- helpers ----------------- */

function toNumber(v) {
  if (typeof v === "number") return v;
  if (typeof v !== "string") return 0;
  const n = Number(v.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function pickWeightedIndexForRows(rows, entriesKey) {
  const total = rows.reduce((sum, row) => sum + Math.max(0, toNumber(row?.[entriesKey])), 0);
  if (total <= 0) return 0;

  const target = cryptoRandomFloat() * total;
  let running = 0;

  for (let i = 0; i < rows.length; i++) {
    running += Math.max(0, toNumber(rows[i]?.[entriesKey]));
    if (target < running) return i;
  }

  return rows.length - 1;
}

function cryptoRandomFloat() {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return arr[0] / 2 ** 32;
  }
  throw new Error("Secure random source unavailable");
}

function detectEntriesKey(headers) {
  const H = (headers || []).map((h) => String(h).toLowerCase());
  const targets = ["entriestotal", "entries_total", "entries", "entry_total", "tickets"];
  for (const t of targets) {
    const i = H.indexOf(t);
    if (i >= 0) return headers[i];
  }
  const j = H.findIndex((h) => h.includes("entries"));
  return j >= 0 ? headers[j] : "";
}

function detectNameKey(headers) {
  const normMap = buildNormalizedKeyMap(headers);
  const full = findByNormalized(normMap, ["name", "fullname", "customername", "full_name"]);
  if (full) return full;

  const hasFirst = findByNormalized(normMap, ["firstname", "first", "first_name"]) != null;
  const hasLast = findByNormalized(normMap, ["lastname", "last", "last_name"]) != null;
  if (hasFirst || hasLast) return "__first_last__";

  return "";
}

function deriveDisplayName(row, nameKey) {
  const { first, last } = deriveFirstLast(row, nameKey);
  const full = `${first} ${last}`.trim();
  if (full) return full;

  if (nameKey && nameKey !== "__first_last__") {
    const v = String(row[nameKey] || "").trim();
    if (v) return v;
  }

  const emailKey = findRowKeyByNormalized(row, ["email", "customeremail", "e-mail"]);
  const e = emailKey ? String(row[emailKey] || "") : "";
  const base = e.split("@")[0] || "Winner";
  return titleCase(base.replace(/[._-]/g, " "));
}

function deriveFirstLast(row, nameKey) {
  const firstKey = findRowKeyByNormalized(row, ["firstname", "first", "first_name", "first name"]);
  const lastKey = findRowKeyByNormalized(row, ["lastname", "last", "last_name", "last name"]);

  const first = titleCase(String((firstKey && row[firstKey]) || "").trim());
  const last = titleCase(String((lastKey && row[lastKey]) || "").trim());
  if (first || last) return { first, last };

  let full = "";
  if (nameKey && nameKey !== "__first_last__") full = String(row[nameKey] || "").trim();
  if (!full) full = String(row["name"] || "").trim();
  if (full) {
    const parts = full.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return { first: titleCase(parts[0]), last: titleCase(parts.slice(1).join(" ")) };
    if (parts.length === 1) return { first: titleCase(parts[0]), last: "" };
  }

  const emailKey = findRowKeyByNormalized(row, ["email", "customeremail", "e-mail"]);
  const e = emailKey ? String(row[emailKey] || "") : "";
  const tokens = (e.split("@")[0] || "").split(/[._-]+/).filter(Boolean);
  if (tokens.length >= 2) return { first: titleCase(tokens[0]), last: titleCase(tokens.slice(1).join(" ")) };
  return { first: titleCase(tokens[0] || "Winner"), last: "" };
}

/* ---------- normalized key helpers ---------- */
function normalizeKey(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function buildNormalizedKeyMap(headers) {
  const map = new Map();
  (headers || []).forEach((h) => map.set(normalizeKey(h), h));
  return map;
}

function findByNormalized(map, candidates) {
  for (const c of candidates) {
    const k = map.get(normalizeKey(c));
    if (k != null) return k;
  }
  for (const [norm, original] of map) {
    if (candidates.map(normalizeKey).includes(norm)) return original;
  }
  return null;
}

function findRowKeyByNormalized(row, candidates) {
  const map = new Map(Object.keys(row || {}).map((k) => [normalizeKey(k), k]));
  return findByNormalized(map, candidates);
}

function titleCase(s) {
  return String(s).replace(/\w\S*/g, (t) => t[0].toUpperCase() + t.slice(1).toLowerCase());
}
